import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { StoreOrderItem, StoreReview, StoreService } from '../../../service/store/store.service';

/**
 * A rating: five stars, an optional comment and the name it is signed with. Under each
 * order line on the order page (owner, 2026-10-04), and at the bottom of every tool page
 * through app-tool-rating (`send`, `existing`, `label`).
 * Every review shows on the site once the owner approves it.
 * Once given it reads back as the stars with an Edit link.
 */
@Component( {
  selector: 'app-store-review-form',
  templateUrl: './store-review-form.component.html',
  styleUrls: [ './store-review-form.component.scss' ],
} )
export class StoreReviewFormComponent implements OnChanges {

  @Input() token = '';
  @Input() item?: StoreOrderItem;
  /** "Furkan Y.": the order's own name, the default signature */
  @Input() defaultName: string | null = null;
  /**
   * Another place to rate than an order line (the tool pages, 2026-10-04): the review read
   * back, the label over the stars, the name placeholder and how a review is sent.
   */
  @Input() existing: StoreReview | null = null;
  @Input() label = 'Rate This Theme';
  @Input() namePlaceholder = 'Verified Buyer';
  @Input() send?: ( rating: number, comment: string, displayName: string ) => Observable<{ ok: boolean; review: StoreReview }>;
  /** the comment and the name must be filled in (the tool pages: no account, owner 2026-10-04) */
  @Input() requireText = false;
  @Output() saved = new EventEmitter<StoreReview>();

  readonly stars = [ 1, 2, 3, 4, 5 ];
  readonly maxComment = 1000;

  review: StoreReview | null = null;
  editing = false;
  rating = 0;
  hover = 0;
  comment = '';
  displayName = '';
  sending = false;
  error = '';

  constructor( private storeService: StoreService, private analyticsService: AnalyticsService ) {
  }

  ngOnChanges(): void {
    this.review = this.item ? this.item.review ?? null : this.existing;
    this.reset();
  }

  /** a star picked: the form opens with it */
  pick( value: number ): void {
    this.rating = value;
    this.editing = true;
    this.error = '';
  }

  edit(): void {
    this.reset();
    this.editing = true;
  }

  cancel(): void {
    this.reset();
  }

  /** the form can be sent: a star, and with requireText a comment and a name */
  get canSubmit(): boolean {
    return this.rating >= 1 && ( !this.requireText || ( !!this.comment.trim() && !!this.displayName.trim() ) );
  }

  submit(): void {
    if ( this.sending || !this.canSubmit ) return;
    this.sending = true;
    this.error = '';
    const request = this.send
      ? this.send( this.rating, this.comment.trim(), this.displayName.trim() )
      : this.storeService.submitReview( this.token, ( this.item as StoreOrderItem ).id, this.rating, this.comment.trim(), this.displayName.trim() );
    request.subscribe( {
      next: ( res ) => {
        this.sending = false;
        this.review = res.review;
        this.editing = false;
        this.saved.emit( res.review );
        if ( this.item ) {
          this.analyticsService.logEvent( 'Store Review Submitted', {
            sku: this.item.sku, platform: this.item.platform, rating: res.review.rating, hasComment: !!res.review.comment,
          } );
        }
      },
      error: ( err ) => {
        this.sending = false;
        this.error = err?.error?.message ? `Could not save: ${ err.error.message }` : 'Could not save your review. Please try again.';
      },
    } );
  }

  /** the stars shown: the hovered one while choosing, else the chosen or saved rating */
  shown(): number {
    return this.hover || this.rating || this.review?.rating || 0;
  }

  get statusNote(): string {
    switch ( this.review?.status ) {
      case 'pending':
        return 'Waiting For Approval';
      case 'approved':
        return 'Published';
      default:
        return '';
    }
  }

  private reset(): void {
    this.editing = false;
    this.hover = 0;
    this.error = '';
    this.rating = this.review?.rating ?? 0;
    this.comment = this.review?.comment ?? '';
    this.displayName = this.review?.displayName ?? this.defaultName ?? '';
  }
}
